import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config";
import { useCurrency } from "../context/CurrencyContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Address from "../components/Address";

// Safe JSON parser for localStorage values
const safeJSON = (key, fallback) => {
  try {
    const value = localStorage.getItem(key);
    if (value === null || value === undefined || value === "") return fallback;
    return JSON.parse(value);
  } catch (e) {
    console.warn("Invalid JSON in localStorage:", key, e);
    return fallback;
  }
};

const CarryCheckout = () => {
  const navigate = useNavigate();
  const { selectedCurrency } = useCurrency();

  // Local state
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [user, setUser] = useState(null);
  const [checkoutOption, setCheckoutOption] = useState("DeliveryAddress");
  const [cartItems, setCartItems] = useState([]);
  const [addressList, setAddressList] = useState([]);
  const [activeView, setActiveView] = useState(localStorage.getItem("activeView") || "wholesale");
  const [orderRef, setOrderRef] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");

  const [billingAddress, setBillingAddress] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    country: "",
    zip: "",
  });

  const [formData, setFormData] = useState({
    paymentMethod: "COD",
  });

  // ------------------------- APPLY MARKUP -------------------------------
  const applyMarkup = useCallback((basePrice) => {
    if (basePrice == null || isNaN(basePrice)) return 0;
    const view = localStorage.getItem("activeView") || activeView || "wholesale";
    
    if (view === "wholesale") return basePrice;

    const storedPayload = safeJSON("priceTable_seller", { data: [] });
    const table = Array.isArray(storedPayload.data) ? storedPayload.data : [];
    if (!table.length) return basePrice;

    const range = table.find((r) => {
      const from = parseFloat(r.from || 0);
      const to = r.to == null || r.to === "" ? Infinity : parseFloat(r.to);
      return basePrice >= from && basePrice <= to;
    });

    if (range) {
      const markupPercent = parseFloat(range.markup || 0);
      // Corrected formula for percentage addition: base + percentage markup
      return basePrice * (1 + markupPercent / 100);
    }

    return basePrice;
  }, [activeView]);

  // Listen for view changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (!e || e.key === "activeView" || e.type === "viewChange") {
        const newView = localStorage.getItem("activeView") || "wholesale";
        setActiveView(newView);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("viewChange", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("viewChange", handleStorageChange);
    };
  }, []);

  // Initialize user and cart from localStorage
  useEffect(() => {
    const storedUser = safeJSON("user", null);
    const storedCart = safeJSON("cartcarry", []);

    setUser(storedUser);
    setCartItems(Array.isArray(storedCart) ? storedCart : []);

    if (Array.isArray(storedCart) && storedCart.length > 0) {
      const firstItem = storedCart[0];
      setOrderRef(firstItem.orderRef || firstItem.OrderReference || "");
      setDeliveryDate(
        firstItem.Deliverydate || firstItem.DeliveryDate || firstItem.deliveryDate || ""
      );
    }

    if (storedUser) {
      const baseAddress = {
        firstName: storedUser.companyName || storedUser.firstName || "",
        lastName: storedUser.lastName || "",
        email: storedUser.email || "",
        phone: storedUser.mobile || storedUser.contactNumber || "",
        address: storedUser.businessAddress || storedUser.address || "",
        city: storedUser.city || "",
        country: storedUser.country || "",
        zip: storedUser.zip || storedUser.postal || "",
      };

      setBillingAddress(baseAddress);
    }
  }, [activeView]);

  // Fetch saved addresses for user
  useEffect(() => {
    const userId = user?.userId || user?.id || user?.UserID;
    if (userId) {
      fetch(`${API_BASE_URL}/Home/GetAddresses?userId=${userId}`)
        .then((res) => res.json())
        .then((addresses) => {
          setAddressList(addresses || []);
        })
        .catch((error) => console.error("Failed to fetch addresses:", error));
    }
  }, [user]);

  // Totals calculations
  const totals = useMemo(() => {
    const priceFactor = (selectedCurrency && selectedCurrency.price) || 1;
    if (!Array.isArray(cartItems) || cartItems.length === 0) {
      return { nonMarkupTotal: 0, markupTotal: 0 };
    }

    let nonMarkupTotal = 0;
    let markupTotal = 0;

    cartItems.forEach((item) => {
      const basePrice = Number(item.productCheckoutPrice) || 0;
      const qty = Number(item.quantity) || 1;

      const priceNoMarkup = basePrice * priceFactor;
      const priceWithMarkup = applyMarkup(basePrice) * priceFactor;

      nonMarkupTotal += priceNoMarkup * qty;
      markupTotal += priceWithMarkup * qty;
    });

    return { 
      nonMarkupTotal: Math.round(nonMarkupTotal), 
      markupTotal: Math.round(markupTotal) 
    };
  }, [cartItems, selectedCurrency, applyMarkup]);

  const subtotal = totals.markupTotal;
  const subnottotal = totals.nonMarkupTotal;

  // Helper: resolve selected address object
  const resolveSelectedAddress = (sel) => {
    if (!sel) return null;
    if (typeof sel === "object") return sel;
    return addressList.find((a) => a?.id == sel || a?.Id == sel || a?.addressId == sel) || null;
  };

  const handleFinalProcess = async () => {
    if (checkoutOption === "DeliveryAddress") {
      if (!selectedAddressId) {
        alert("Please select a delivery address.");
        return;
      }

      const selectedAddressObj = resolveSelectedAddress(selectedAddressId);
      if (!selectedAddressObj) {
        alert("Invalid delivery address. Please reselect.");
        return;
      }

      await submitOrder(selectedAddressObj);
    } else if (checkoutOption === "BillingAddress") {
      if (!billingAddress || !billingAddress.firstName) {
        alert("Billing address is missing or incomplete.");
        return;
      }
      await submitOrder(billingAddress);
    }
  };

  const submitOrder = async (addressObj) => {
    const storedUser = safeJSON("user", null) || user;
    const userId = storedUser?.userId ?? storedUser?.id ?? storedUser?.UserID ?? null;
    const companyId = storedUser?.companyId;
    const staffname = storedUser?.staffName || "";

    if (!Array.isArray(cartItems) || cartItems.length === 0) {
      alert("Your cart is empty.");
      return;
    }

    const toNum = (v, d = 0) => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : d;
    };

    const finalTotal = activeView === "wholesale" ? Math.round(subnottotal) : Math.round(subtotal);

    const orderData = {
      UserID: userId,
      CompanyId: companyId,
      FirstName: addressObj.firstName || addressObj.FirstName || addressObj.companyName || "",
      LastName: addressObj.lastName || addressObj.LastName || "",
      Phone: addressObj.phone || addressObj.Phone || "",
      Email: storedUser?.email || addressObj.email || "",
      Country: addressObj.country || addressObj.Country || "",
      Address: addressObj.address || addressObj.Address || "",
      City: addressObj.city || addressObj.City || "",
      ZipCode: addressObj.zip || addressObj.ZipCode || "",
      PaymentMethod: formData.paymentMethod || "COD",
      ShippingMethod: "Stockandcarry",
      TotalAmount: finalTotal,
      Rate: Math.round(subnottotal),
      OrderReference: orderRef || "",
      DeliveryDate: deliveryDate || "",
      MetalName: staffname || "",
      OrderItems: cartItems.map((item) => {
        const base = Number(item.productCheckoutPrice) || 0;
        const priceFactor = (selectedCurrency && selectedCurrency.price) || 1;

        const noMarkup = Math.round(base * priceFactor);
        const withMarkup = Math.round(applyMarkup(base) * priceFactor);
        const qty = Number(item.quantity) || 1;

        return {
          ProductID: item.id || item.productId || 0,
          ProductName: item.productName || item.name || "",
          ProductCode: item.selectedValues?.ProductCodes || "",
          DesignNo: item.designNo || "",
          Engrave: item.selectedOptions?.engrave || "",
          NetWt: toNum(item.netWt, 0),
          ProductDescription: item.productDescription || "",
          Currency: (selectedCurrency && selectedCurrency.name) || "",
          ProductPrice: noMarkup,
          ProductMarkupPrice: withMarkup,
          Quantity: qty,
          ProductImage: item.selectedOptions?.productimg || "",
          Shape: item.selectedOptions?.shape || "",
          BandWidth: item.selectedOptions?.bandWidth || "",
          Certificate: item.selectedValues?.Certificate || "",
          Clarity: item.selectedValues?.Clarity || "",
          Colour: item.selectedValues?.Colour || "",
          CutGrade: "Stockandcarry",
          //CutGrade: item.selectedValues?.CutGrade ?? item.selectedValues?.Cutgrade ?? "",
          Polish: item.selectedValues?.Polish || "",
          Symmetry: item.selectedValues?.Symmetry || "",
          Comments: item.comment || "",
          Fluo: item.selectedValues?.Fluo || "",
          DiamondStone: item.selectedOptions?.stone || "",
          MetalName: item.selectedOptions?.metal || "",
          Carat: toNum(item.selectedValues?.Carat ?? item.selectedOptions?.carat, 0),
          Orderlogo: item.Orderlogo || "",
          OrderRef: item.OrdersRef || item.orderRef || "",
        };
      }),
    };

    try {
      const response = await fetch(`${API_BASE_URL}/Order/PlaceOrder`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(orderData),
      });

      const result = await response.json().catch(() => null);
      if (response.ok) {
        alert("Order Placed Successfully!");
        localStorage.removeItem("cartcarry");
        setCartItems([]);
        navigate("/products");
      } else {
        alert(`Error: ${result?.message || response.statusText}`);
      }
    } catch (error) {
      console.error("Order submission failed:", error);
      alert("Something went wrong. Please try again.");
    }
  };

  if (!selectedCurrency || !selectedCurrency.price) {
    return (
      <div className="home">
        <Header />
        <div style={{ padding: 40, textAlign: "center" }}>Loading checkout...</div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="home">
      <div id="page" className="hfeed page-wrapper">
        <Header />
        <div id="site-main" className="site-main">
          <div id="main-content" className="main-content">
            <div id="primary" className="content-area">
              <br />
              <div id="content" className="site-content" role="main">
                <div className="section-padding">
                  <div className="section-container p-l-r">
                    <div className="shop-checkout">
                      <div className="row">
                        <div className="col-xl-7 col-lg-7 col-md-12 col-12">
                          <div className="customer-details">
                            <div className="checkout-options">
                              <div className="option-select">
                                <label>
                                  <input
                                    type="radio"
                                    name="checkoutOption"
                                    value="DeliveryAddress"
                                    checked={checkoutOption === "DeliveryAddress"}
                                    onChange={() => setCheckoutOption("DeliveryAddress")}
                                  />{" "}
                                  Delivery Address
                                </label>
                                <label>
                                  <input
                                    type="radio"
                                    name="checkoutOption"
                                    value="BillingAddress"
                                    checked={checkoutOption === "BillingAddress"}
                                    onChange={() => setCheckoutOption("BillingAddress")}
                                  />{" "}
                                  Billing Address
                                </label>
                              </div>

                              <div className="option-content">
                                {checkoutOption === "BillingAddress" && (
                                  <div className="content-guest">
                                    <div className="billing-fields">
                                      <h3>Billing Details</h3>
                                      <div className="billing-fields-wrapper">
                                        <p className="form-row">
                                          <label>Company name *</label>
                                          <input type="text" readOnly value={billingAddress.firstName} />
                                        </p>
                                        <p className="form-row">
                                          <label>Email *</label>
                                          <input type="email" readOnly value={billingAddress.email} />
                                        </p>
                                        <p className="form-row">
                                          <label>Phone *</label>
                                          <input type="tel" readOnly value={billingAddress.phone} />
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {checkoutOption === "DeliveryAddress" && (
                                  <div className="content-register">
                                    <h3>Delivery Address</h3>
                                    <Address
                                      userId={user?.userId || user?.id || user?.UserID}
                                      companyId={user?.companyId}
                                      onAddressSelect={(id) => setSelectedAddressId(id)}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Order Summary */}
                        <div className="col-xl-5 col-lg-5 col-md-12 col-12">
                          <div className="checkout-review-order">
                            <div className="checkout-review-order-table">
                              <h3 className="review-order-title">Product</h3>
                              <div className="cart-items">
                                {Array.isArray(cartItems) && cartItems.length > 0 ? (
                                  cartItems.map((item, index) => {
                                    const base = Number(item.productCheckoutPrice) || 0;
                                    const qty = Number(item.quantity) || 1;
                                    const priceFactor = selectedCurrency?.price || 1;
                                    const unitPrice = activeView === "wholesale" ? base : applyMarkup(base);
                                    const itemTotal = Math.round(unitPrice * priceFactor * qty);

                                    return (
                                      <div className="cart-item" key={item.id || index}>
                                        <div className="info-product">
                                          <div className="thumbnail-name">
                                            <div className="product-thumbnail">
                                              <img
                                                src={item.selectedOptions?.productimg || "/assets/img/default.jpg"}
                                                alt={item.productName || "Product image"}
                                              />
                                            </div>
                                            <div className="product-name">
                                              {item.productName}
                                              <strong className="product-quantity"> QTY: {qty}</strong>
                                              {item.comment && (
                                                <strong className="product-quantity"> Comment: {item.comment}</strong>
                                              )}
                                            </div>
                                          </div>

                                          <div className="product-total">
                                            <span>
                                              {selectedCurrency?.name || ""} {itemTotal}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })
                                ) : (
                                  <p>No items in cart.</p>
                                )}
                              </div>

                              <div className="order-total">
                                <h2>Total</h2>
                                <div className="total-price">
                                  <strong>
                                    {selectedCurrency?.name || ""}{" "}
                                    {activeView === "wholesale" ? Math.round(subnottotal) : Math.round(subtotal)}
                                  </strong>
                                </div>
                              </div>

                              <button
                                type="button"
                                onClick={handleFinalProcess}
                                className="button update-cart"
                                id="place-btn"
                              >
                                Place Order
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <Footer />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarryCheckout;